import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { clearSearch } from '../common/store/filtersSlice';
import { getProductsCount } from '../common/store/productsSlice';
import { selectLocationSelectionRequired } from '../common/store/locationsSlice';

export default function SearchContext({ invalidDates = false }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const filters = useSelector(state => state.filters);
    const products = useSelector(state => state.products);
    const settings = useSelector(state => state.settings.settings);
    const locations = useSelector(state => state.locations);
    const locationRequired = useSelector(selectLocationSelectionRequired);
    if (!filters.search) return null;

    const multiLocation = settings.allow_multi_location_cart;
    const location = [...locations.userLocations, ...(settings.warehouse_location || [])]
        .find(item => String(item.id) === String(filters.location));
    const missingLocation = locationRequired && String(filters.location) === '0';
    const locationLabel = multiLocation || locations.locationsNotDefined
        ? t('searchContext.allLocations')
        : missingLocation ? t('searchContext.chooseLocation')
        : location?.name || t('searchContext.loadingLocation');
    const categoryName = filters.categoryPath.map(item => item.name).join(' / ');
    const specifications = Object.entries(filters.specificationsFilters);
    const restricted = filters.category !== '' || specifications.length > 0;
    const failed = products.status === 'failed';
    const ready = products.countReady && !missingLocation && !invalidDates;

    return (
        <Paper component="section" variant="outlined" aria-label={t('searchContext.label')}
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, flexShrink: 0 }}>
            <Stack spacing={1.5}>
                <Box aria-live="polite" aria-atomic="true">
                    <Typography variant="h6" component="h2">
                        {t(ready ? 'searchContext.results' : 'searchContext.query', {
                            count: products.productsCount, term: filters.submittedSearchCriteria
                        })}
                    </Typography>
                    <Typography>{t('searchContext.location', { location: locationLabel })}</Typography>
                    <Typography>{t('searchContext.scope', {
                        scope: filters.category === '' ? t('searchContext.allCategories')
                            : t('searchContext.category', { category: categoryName || filters.category })
                    })}</Typography>
                    {specifications.length > 0 && <Typography>
                        {t('searchContext.filters', {
                            filters: specifications.map(([name, value]) => `${name}: ${value}`).join(' · ')
                        })}
                    </Typography>}
                </Box>
                {missingLocation ? <Alert severity="info">{t('searchContext.chooseLocation')}</Alert>
                    : invalidDates ? <Alert severity="info">{t('searchContext.checkDates')}</Alert>
                    : failed ? <Alert severity="error" action={
                        <Button color="inherit" onClick={() => dispatch(getProductsCount({}))}>
                            {t('searchContext.retry')}
                        </Button>
                    }>{t('searchContext.error')}</Alert>
                    : !ready ? <Typography role="status">{t('searchContext.loading')}</Typography>
                    : products.productsCount === 0 && <Alert severity="info">
                        {t(restricted ? 'searchContext.emptyFiltered' : 'searchContext.empty')}
                    </Alert>}
                <Box>
                    <Button variant="outlined" onClick={() => dispatch(clearSearch())}>
                        {t('searchContext.clear')}
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
}
